import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../lib/utils';
import { TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProfitTooltip({ book, position }) {
  const [profitData, setProfitData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfit = async () => {
      if (!book?.book_id) return;
      
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/api/calculate-profit?book_id=${book.book_id}&purchase_price=${book.used_price || 5}&supplier_country=${book.seller_country || 'GB'}`,
          {
            method: 'POST',
            credentials: 'include',
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setProfitData(data);
        }
      } catch (error) {
        console.error('Failed to fetch profit:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfit();
  }, [book]);

  if (!book) return null;

  const isProfitable = profitData?.is_profitable ?? (book.expected_profit >= 1);
  const profit = profitData?.profit ?? book.expected_profit ?? 0;

  // Calculate tooltip position to stay within viewport
  const tooltipStyle = {
    position: 'fixed',
    left: Math.min(position.x, window.innerWidth - 320),
    top: Math.max(10, Math.min(position.y, window.innerHeight - 300)),
    zIndex: 9999,
  };

  return (
    <div 
      className="profit-tooltip w-72 animate-fade-in"
      style={tooltipStyle}
      data-testid="profit-tooltip"
    >
      <div className="profit-tooltip-title flex items-center gap-2">
        <TrendingUp className="h-4 w-4" />
        REAL-TIME PROFIT ANALYSER
      </div>

      {loading ? (
        <div className="py-4 text-center text-zinc-500">Calculating...</div>
      ) : (
        <div className="space-y-3">
          {/* Buy/Sell Prices */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="profit-tooltip-label">Buy It For</div>
              <div className="profit-tooltip-value">
                {formatCurrency(profitData?.buy_price || book.used_price)}
              </div>
            </div>
            <div>
              <div className="profit-tooltip-label">Sell It For</div>
              <div className="profit-tooltip-value">
                {formatCurrency(profitData?.sell_price || book.amazon_price)}
              </div>
            </div>
          </div>

          {/* Fees Breakdown */}
          <div className="border-t border-zinc-800 pt-3 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-zinc-500">Amazon Fees (15%)</span>
              <span className="text-zinc-400 font-mono">
                -{formatCurrency(profitData?.amazon_fee || (book.amazon_price * 0.15))}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-zinc-500">Prep Fee</span>
              <span className="text-zinc-400 font-mono">
                -{formatCurrency(profitData?.prep_fee || 2.5)}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-zinc-500">Inbound Shipping</span>
              <span className="text-zinc-400 font-mono">
                -{formatCurrency(profitData?.inbound_shipping || 1.5)}
              </span>
            </div>
            {profitData?.import_breakdown?.vat > 0 && (
              <>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Import VAT</span>
                  <span className="text-zinc-400 font-mono">
                    -{formatCurrency(profitData.import_breakdown.vat)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Import Duty</span>
                  <span className="text-zinc-400 font-mono">
                    -{formatCurrency(profitData.import_breakdown.duty)}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Profit */}
          <div className="border-t border-zinc-800 pt-3">
            <div className="flex justify-between items-center">
              <span className="profit-tooltip-label">Profit After All Fees</span>
              <span className={`text-lg font-bold font-mono flex items-center gap-1 ${isProfitable ? 'profit-tooltip-profit' : 'profit-tooltip-loss'}`}>
                {isProfitable ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                {formatCurrency(profit)}
              </span>
            </div>
          </div>

          {/* Warning for low profit */}
          {!isProfitable && (
            <div className="flex items-center gap-2 p-2 bg-rose-500/10 rounded border border-rose-500/20 text-rose-400 text-xs">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>Profit must be greater than £1 for viable arbitrage</span>
            </div>
          )}

          {/* Import Warning */}
          {book.seller_country && book.seller_country !== 'GB' && (
            <div className="flex items-center gap-2 p-2 bg-orange-500/10 rounded border border-orange-500/20 text-orange-400 text-xs">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>Import VAT payable – verify margin</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
